import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { ChangeEvent, useEffect, useState } from "react";
import useTranslation from "../../hooks/useTranslation";
import { SurveyRecipient } from "../../types/survey";
import { Button } from "../ui/Button/Button";
import { Typography } from "../ui/Typography/Typography";
import { readCSVFile } from "./UploadFile.utils";
import "./UploadFile.scss";

const accept = ".csv";

export interface UploadFileProps {
  label: string;
  onRecipientsUpload: (recipients: SurveyRecipient[]) => void;
  fileUploaded?: boolean;
}

export const UploadFile = ({
  label,
  onRecipientsUpload,
  fileUploaded = false,
}: UploadFileProps) => {
  const { t } = useTranslation("surveyLaunchPage");
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (selectedFile && fileUploaded) {
      setSelectedFile(undefined);
      setErrorMsg(""); // Clear any previous errors on successful upload
    }
  }, [selectedFile, fileUploaded]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMsg("");
    setIsProcessing(true);

    const isCSV =
      file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv";

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxFileSize) {
      setErrorMsg(
        t("shareLink.upload.errors.fileSize") ||
          "File size too large (max 10MB)"
      );
      setIsProcessing(false);
      return;
    }

    if (!isCSV) {
      setErrorMsg(
        t("shareLink.upload.errors.fileType") || "Please upload a CSV file"
      );
      setIsProcessing(false);
      return;
    }

    // Check if file is actually empty
    if (file.size === 0) {
      setErrorMsg(t("shareLink.upload.errors.emptyFile") || "File is empty");
      setIsProcessing(false);
      return;
    }

    setSelectedFile(file);

    try {
      const recipients = await readCSVFile(file, t);

      if (recipients.length > 0) {
        onRecipientsUpload(recipients);
      } else {
        setErrorMsg(
          t("shareLink.upload.errors.noEmails") ||
            "No valid email addresses found"
        );
      }
    } catch (error) {
      setErrorMsg(
        error instanceof Error ? error.message : "Error reading file"
      );
    } finally {
      setIsProcessing(false);
      event.target.value = "";
    }
  };

  return (
    <label className="UploadFile" htmlFor={label}>
      <input
        id={label}
        type="file"
        onChange={handleFileChange}
        accept={accept}
        hidden
      />
      <Button
        className="UploadFile__button"
        variant="contained"
        color="secondary"
        component="span"
        small
        disabled={isProcessing}
        loading={isProcessing}
      >
        <FileUploadOutlinedIcon />
        {label}
      </Button>
      {errorMsg && (
        <div className="UploadFile__error">
          <Typography desktop="caption" color="error">
            &nbsp;{errorMsg}
          </Typography>
        </div>
      )}
    </label>
  );
};
